import { useState, useEffect } from "react";
import { connect } from "react-redux";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  LineElement,
  BarElement,
  PointElement,
  BarController,
  BubbleController,
  DoughnutController,
  LineController,
  PieController,
  PolarAreaController,
  RadarController,
  ScatterController,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  RadialLinearScale,
  TimeScale,
  TimeSeriesScale,
  Decimation,
  Filler,
  Title,
  SubTitle,
} from "chart.js";
import { Chart } from "react-chartjs-2";

import GET_CAPEX from "../trivial_components/GET_CAPEX";
import Modal from "../common_modal/Modal";

import { replace_features } from "../../App/actions/siniActions";
import { filter_isi_layer } from "../../App/actions/dataActions";

import { calculateWithoutConvert } from "../../App/validation/convert_data";
import generate_array_color from "../../App/validation/generate_array_color";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  LineElement,
  BarElement,
  PointElement,
  BarController,
  BubbleController,
  DoughnutController,
  LineController,
  PieController,
  PolarAreaController,
  RadarController,
  ScatterController,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  RadialLinearScale,
  TimeScale,
  TimeSeriesScale,
  Decimation,
  Filler,
  Title,
  SubTitle
);

const CUSTOM_CHART = ({
  layer,
  properties,
  replace_features,
  filter_isi_layer,
  auth,
}) => {
  const [modal_npv, set_modal_npv] = useState(false);
  const [modal_irr, set_modal_irr] = useState(false);

  const toggle_npv = () => {
    set_modal_npv(!modal_npv);
  };

  const toggle_irr = () => {
    set_modal_irr(!modal_irr);
  };

  const calculate_local = (source) => {
    const { layer_id, geo_layer_list } = layer;
    const { capex_value } = properties;
    const layer_object = geo_layer_list.find(
      (item) => item?.geo_layer?._id === layer_id
    )?.geo_layer;
    const features = layer_object?.geojson?.features;
    const fields = layer_object?.fields;
    if (features && fields && capex_value && layer_id) {
      const features_new = calculateWithoutConvert({
        features,
        fields,
        source,
        capex_value,
      });
      replace_features({
        geo_layer_id: layer_id,
        features: features_new,
      });
    }
  };

  const on_filter_isi_layer = (value, field_key) => {
    const { layer_id } = layer;
    if (layer_id && field_key) {
      const body = {
        geo_layer_id: layer_id,
        field_key,
        value,
      };
      filter_isi_layer(body);
    }
  };

  useEffect(() => {
    calculate_local("componentDidMount");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    calculate_local("componentDidUpdate");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties.capex_value, layer.layer_id]);

  const { layer_id, geo_layer_list } = layer;
  const { capex_value } = properties;

  const layer_object = geo_layer_list.find(
    (item) => item?.geo_layer?._id === layer_id
  )?.geo_layer;
  let chart_list = layer_object?.chart_list || [];
  const features = layer_object?.geojson?.features || [];

  let irr_value = 0;
  let npv_value = 0;
  let npv_array = [];

  let npv_info;
  let irr_info;

  chart_list = chart_list.map((item) => {
    const {
      ina,
      x_axis_field_key,
      y_axis_field_key,
      x_element_custom_order,
      color_default,
      is_npv_kai,
      wacc_field_key,

      is_use_y_axis,
      is_use_custom_order,
    } = item;

    let labels = [];

    if (is_use_custom_order) {
      labels = x_element_custom_order;
    } else {
      labels = features.map((feature) => {
        return feature?.properties?.[x_axis_field_key];
      });
      labels = labels.sort();
    }

    let data = [];

    if (is_use_y_axis) {
      features.forEach((feature) => {
        const data_index = labels.findIndex(
          (label) =>
            String(label) === String(feature.properties[x_axis_field_key])
        );
        const init_value = data?.[data_index] || 0;
        let final_value = isNaN(feature.properties[y_axis_field_key])
          ? 0
          : parseInt(feature.properties[y_axis_field_key]);
        if (init_value) {
          final_value = init_value + final_value;
        }
        data[data_index] = final_value;
      });
    } else {
      features.forEach((feature) => {
        const data_index = labels.findIndex(
          (label) =>
            String(label) === String(feature.properties[x_axis_field_key])
        );
        const init_value = data?.[data_index] || 0;
        let final_value = 1;
        if (init_value) {
          final_value = init_value + final_value;
        }
        data[data_index] = final_value;
      });
    }

    if (is_npv_kai) {
      function create_npv_function_from_array(array_income, array_wacc) {
        return function y() {
          let result = 0;
          for (let i = 0; i < array_income.length; i++) {
            result += array_income[i] / Math.pow(1 + array_wacc[i], i + 1);
          }
          return result;
        };
      }
      const array_income = data;
      const array_wacc = features.map(
        (item) => parseFloat(item?.properties?.[wacc_field_key]) / 100
      );
      const y_function = create_npv_function_from_array(
        array_income,
        array_wacc
      );
      npv_value = parseInt(y_function());

      const wacc = (
        parseFloat(features?.[0]?.properties?.[wacc_field_key]) / 100
      ).toFixed(4);

      npv_info = (
        <main>
          <h1 className="text_header margin_bottom">Rumus perhitungan NPV</h1>

          <table
            style={{ backgroundColor: "#ffffff", borderRadius: "7px" }}
            className="table_padding"
          >
            <tbody>
              <tr>
                <td>NPV</td>
                <td style={{ paddingBottom: "7px" }}>=</td>
                <td>
                  {x_element_custom_order.map((year, idx) => {
                    return (
                      <table key={idx} className="inline">
                        <tbody className="text_center">
                          <tr>
                            <td
                              style={{
                                borderBottom: "1px solid black",
                              }}
                            >
                              Net cash flow {year}
                            </td>
                            <td
                              rowSpan={2}
                              style={{
                                verticalAlign: "center",
                                paddingBottom: "7px",
                              }}
                            >
                              {idx !== x_element_custom_order.length - 1 &&
                                " + "}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              ( 1 + WACC) <sup>{idx + 1}</sup>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    );
                  })}
                </td>
              </tr>
              <tr>
                <td></td>
                <td style={{ paddingBottom: "7px" }}>=</td>
                <td>
                  {data.map((value, idx) => {
                    return (
                      <table key={idx} className="inline">
                        <tbody>
                          <tr>
                            <td
                              style={{
                                borderBottom: "1px solid black",
                              }}
                            >
                              {new Intl.NumberFormat("id-ID", {
                                style: "decimal",
                              }).format(value)}
                            </td>
                            <td
                              rowSpan={2}
                              style={{
                                verticalAlign: "center",
                                paddingBottom: "7px",
                              }}
                            >
                              {idx !== data.length - 1 && " + "}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              ( 1 + {wacc}) <sup>{idx + 1}</sup>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    );
                  })}
                </td>
              </tr>
              <tr>
                <td></td>
                <td>=</td>
                <td>
                  {new Intl.NumberFormat("id-ID", {
                    style: "decimal",
                  }).format(npv_value)}
                </td>
              </tr>
            </tbody>
          </table>
          <section className="container_light">
            <p className="text_small">
              Catatan: Net cash flow pada tahun pertama menggunakan nilai
              negatif dari CAPEX penataan, jika nilai CAPEX belum dihitung maka
              perhitungan NPV belum bisa dilakukan dan nilainya menjadi tidak
              sesuai.
            </p>
          </section>
        </main>
      );

      function create_y_function(array_income) {
        return function y(x) {
          let result = 0;
          for (let i = 0; i < array_income.length; i++) {
            result += array_income[i] / Math.pow(1 + x, i + 1);
          }
          return result;
        };
      }

      const y = create_y_function(array_income);

      function find_root_recursive(a, b, tolerance) {
        if (b - a < tolerance) {
          return (a + b) / 2;
        }

        let mid = (a + b) / 2;
        let f_mid = y(mid);

        if (f_mid === 0) {
          return mid;
        } else if (f_mid * y(a) < 0) {
          return find_root_recursive(a, mid, tolerance);
        } else {
          return find_root_recursive(mid, b, tolerance);
        }
      }

      irr_value = find_root_recursive(-1000, 1000, 1e-10);

      irr_info = (
        <main>
          <h1 className="text_header margin_bottom">Rumus perhitungan IRR</h1>
          <p className="text_small">
            Rumus IRR diambil menggunakan rumus NPV dengan menghitung WACC
            ketika NPV sama dengan nol.
          </p>

          <table
            style={{ backgroundColor: "#ffffff", borderRadius: "7px" }}
            className="table_padding"
          >
            <tbody>
              <tr>
                <td>NPV</td>
                <td style={{ paddingBottom: "7px" }}>=</td>
                <td>
                  {x_element_custom_order.map((year, idx) => {
                    return (
                      <table key={idx} className="inline">
                        <tbody className="text_center">
                          <tr>
                            <td
                              style={{
                                borderBottom: "1px solid black",
                              }}
                            >
                              Net cash flow {year}
                            </td>
                            <td
                              rowSpan={2}
                              style={{
                                verticalAlign: "center",
                                paddingBottom: "7px",
                              }}
                            >
                              {idx !== x_element_custom_order.length - 1 &&
                                " + "}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              ( 1 + WACC) <sup>{idx + 1}</sup>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    );
                  })}
                </td>
              </tr>
              <tr>
                <td>0</td>
                <td style={{ paddingBottom: "7px" }}>=</td>
                <td>
                  {data.map((value, idx) => {
                    return (
                      <table key={idx} className="inline">
                        <tbody>
                          <tr>
                            <td
                              style={{
                                borderBottom: "1px solid black",
                              }}
                            >
                              {new Intl.NumberFormat("id-ID", {
                                style: "decimal",
                              }).format(value)}
                            </td>
                            <td
                              rowSpan={2}
                              style={{
                                verticalAlign: "center",
                                paddingBottom: "7px",
                              }}
                            >
                              {idx !== data.length - 1 && " + "}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              ( 1 + IRR) <sup>{idx + 1}</sup>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    );
                  })}
                </td>
              </tr>
              <tr>
                <td>IRR</td>
                <td>≈</td>
                <td>{irr_value}</td>
              </tr>
            </tbody>
          </table>
          <section className="container_light">
            <p className="text_small">
              Catatan: Pada kasus ini nilai IRR adalah pendekatan dari solusi
              persamaan polinomial orde 6 (tergantung rentang jumlah tahun),
              angka IRR yang dihasilkan mungkin tidak menghasilkan NPV yang
              eksak sama dengan 0.
            </p>
          </section>
        </main>
      );
    }
    const chart_js_data = {
      labels,
      datasets: [
        {
          label: ina,
          labels,
          data,
          backgroundColor: color_default,
        },
      ],
    };
    item.chart_js_data = chart_js_data;
    item.npv_value = npv_value;
    item.npv_array = npv_array;
    return item;
  });

  const modal_npv_content = modal_npv && (
    <Modal modalSize="large" id="modal" isOpen={modal_npv} onClose={toggle_npv}>
      <div className="box-body">
        <main className="max_height">{npv_info}</main>
      </div>
    </Modal>
  );

  const modal_irr_content = modal_irr && (
    <Modal modalSize="large" id="modal" isOpen={modal_irr} onClose={toggle_irr}>
      <div className="box-body">
        <main className="max_height">{irr_info}</main>
      </div>
    </Modal>
  );

  return (
    <main>
      {modal_npv_content}
      {modal_irr_content}
      {chart_list.map((item, idx) => {
        const {
          is_npv_kai,
          npv_value,
          chart_type,
          x_axis_field_key,
          chart_js_data,
        } = item;

        let chartData = { ...chart_js_data };
        const label_array = chartData.datasets[0].labels;
        const value_array = chartData.datasets[0].data;
        const color_array = generate_array_color(label_array);
        chartData.datasets[0].backgroundColor = color_array;
        chartData.datasets[0].borderColor = color_array;
        chartData.datasets[0].borderWidth = 1;

        const is_circle_chart = ["pie", "doughnut", "polarArea"].includes(
          chart_type
        );
        const isLongChart = ["bar", "line", "area"].includes(chart_type);
        const chartHeight = isLongChart ? 150 : undefined;

        // Calculate total and percentages for legend
        const numericData = value_array.map((d) =>
          typeof d === "object" && d !== null ? d.y : d
        );
        const total = numericData.reduce((sum, val) => sum + val, 0);

        const chartOptions = {
          responsive: true,
          maintainAspectRatio: !isLongChart,
          plugins: {
            legend: { display: false },
            tooltip: { enabled: true },
          },
          scales: isLongChart
            ? {
                x: {
                  beginAtZero: true,
                  ticks: { display: chart_type === "line" },
                },
                y: {
                  beginAtZero: true,
                },
              }
            : {},
          onClick: (event, element) => {
            try {
              const index = element?.[0]?.index;
              const value = label_array?.[index];
              if (value !== undefined) {
                on_filter_isi_layer(value, x_axis_field_key);
              }
            } catch (e) {}
          },
        };

        const custom_legend = (
          <section
            className="container background_white"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "8px",
              maxHeight: "200px",
              overflowY: "auto",
              overflowX: "hidden",
              borderRadius: "5px",
            }}
          >
            <table>
              <tbody>
                {label_array.map((label, index) => {
                  const count = numericData[index];
                  const percentage =
                    total > 0 ? ((count / total) * 100).toFixed(1) : "0.0";
                  return (
                    <tr
                      key={index}
                      style={{ whiteSpace: "nowrap", cursor: "pointer" }}
                      onClick={() =>
                        on_filter_isi_layer(label, x_axis_field_key)
                      }
                    >
                      <td
                        style={{
                          verticalAlign: "top",
                          paddingRight: "8px",
                          textAlign: "center",
                          width: "30px",
                        }}
                      >
                        <div
                          style={{
                            width: "15px",
                            height: "15px",
                            backgroundColor:
                              color_array[index] || "transparent",
                            margin: "0 auto",
                          }}
                        />
                      </td>
                      <td>
                        <div>
                          <p className="text_medium">{label} </p>
                          <span className="text_small">{count}</span>{" "}
                          <span className="text_small sub_color">
                            ({percentage} %)
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        );

        let final_content;

        if (is_circle_chart) {
          final_content = (
            <main
              style={{
                display: "flex",
                width: "100%",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: "10px",
              }}
            >
              <section style={{ width: "77px" }}>
                <Chart
                  type={chart_type}
                  data={chartData}
                  options={chartOptions}
                  style={{ height: chartHeight, maxHeight: chartHeight }}
                />
              </section>
              <section style={{ width: "100%" }}>{custom_legend}</section>
            </main>
          );
        } else {
          final_content = (
            <main style={{ width: "100%" }}>
              <div
                style={{
                  overflowX: "auto",
                  paddingBottom: "1rem",
                }}
              >
                <div
                  style={{ minWidth: `${label_array.length * 30}px` }}
                  className="container background_white"
                >
                  <Chart
                    type={chart_type}
                    data={chartData}
                    options={chartOptions}
                    style={{ height: chartHeight, maxHeight: chartHeight }}
                  />
                </div>
              </div>
              {custom_legend}
            </main>
          );
        }

        return (
          <section
            key={idx}
            className="container background_grey_light outline_grey margin_bottom"
          >
            <h2 className="text_medium sub_color margin_bottom">{item.ina}</h2>
            {final_content}

            {is_npv_kai && (
              <>
                <section className="container_light margin_bottom">
                  <section>
                    <h2 className="text_medium inline margin_right">
                      PERHITUNGAN NPV
                    </h2>
                    <div
                      className="badge_pill_small background_black inline cursor_pointer"
                      onClick={toggle_npv}
                    >
                      i
                    </div>
                  </section>

                  <br />
                  <p className="text_bold">
                    {new Intl.NumberFormat("id-ID", {
                      style: "decimal",
                    }).format(npv_value)}
                  </p>
                  {!capex_value && (
                    <section className="container_light background_red">
                      <p className="text_small margin_bottom">
                        Nilai CAPEX belum dimuat, nilai NPV & IRR belum final,
                        klik tombol di bawah untuk memuat nilai CAPEX terlebih
                        dahulu.
                      </p>
                      <GET_CAPEX />
                    </section>
                  )}
                </section>
                <section className="container_light margin_bottom">
                  <section>
                    <h2 className="text_medium inline margin_right">
                      PERHITUNGAN IRR
                    </h2>
                    <div
                      className="badge_pill_small background_black inline cursor_pointer"
                      onClick={toggle_irr}
                    >
                      i
                    </div>
                  </section>

                  <br />
                  <h1 className="text_bold">{irr_value}</h1>
                </section>
              </>
            )}
          </section>
        );
      })}
    </main>
  );
};

const mapStateToProps = (state) => ({
  map: state.map,
  layer: state.layer,
  properties: state.properties,
  auth: state.auth,
});

export default connect(mapStateToProps, { replace_features, filter_isi_layer })(
  CUSTOM_CHART
);
